// Wave 10 (port-plan 24) — pure helpers for the Generate Sales
// Scenarios view. Kept out of the .tsx view file (same pattern as
// roundtableCopy.ts / searchQaCopy.ts) so they can be unit-tested
// directly and the view file only exports components.

import {
  isApiError,
  type JobStatus,
  type ScenarioGeneration,
  type ScenarioPreviewOut,
  type ScenarioPoolMember,
  type TeamOut,
} from "../lib/api";
import { ACTIVE_POLL_MS } from "../lib/polling";

/** Plan 31 — the team picked first when it is enabled. Only a starting
 * preference: whether a team can generate scenarios comes from its
 * `scenarios_enabled` flag, never from its name. */
export const PREFERRED_SCENARIO_TEAM_NAME = "PNS Sales";

/** Notice policy edits from other sessions without rerunning analysis. */
export const SCENARIO_POLICY_POLL_MS = 15_000;

/** The team the page opens on: the enabled "PNS Sales" team if there is
 * one, otherwise the first enabled team in list order, otherwise null
 * (the page then shows its no-enabled-teams empty state). */
export function pickDefaultScenarioTeam(
  teams: readonly TeamOut[],
): TeamOut | null {
  const enabled = teams.filter((t) => t.scenarios_enabled);
  return (
    enabled.find((t) => t.name === PREFERRED_SCENARIO_TEAM_NAME) ??
    enabled[0] ??
    null
  );
}

/** The server's refusal when a super admin has turned scenarios off for
 * the team (Plan 31 locked contract: 403 with detail
 * `{"error": "scenarios_disabled", …}`). */
export function isScenariosDisabledError(err: unknown): boolean {
  return (
    isApiError(err) && err.status === 403 && err.reason === "scenarios_disabled"
  );
}

/** Derive the inclusive 7-day window ending on `endDate` (YYYY-MM-DD).
 * Returns `{ start, end }` as YYYY-MM-DD strings, or null when the
 * input is not a valid date. E.g. end 2026-07-13 → start 2026-07-07.
 *
 * The strings are NZ calendar dates (the backend resolves them to NZ
 * midnight boundaries per ADR 0017); the subtraction itself is pure
 * calendar arithmetic, done in UTC space because UTC has no DST so the
 * date roll-over is exact — same trick as nzMidnightMs in
 * TimeRangeControls.
 */
export function deriveScenarioWindow(
  endDate: string,
  lookbackDays = 6,
): { start: string; end: string } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return null;
  const end = new Date(`${endDate}T00:00:00Z`);
  if (Number.isNaN(end.getTime()) || end.toISOString().slice(0, 10) !== endDate || ![6, 12, 18, 24].includes(lookbackDays)) return null;
  const start = new Date(end.getTime() - lookbackDays * 24 * 60 * 60 * 1000);
  return { start: start.toISOString().slice(0, 10), end: endDate };
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "7 Jul" for "2026-07-07". Deterministic (no locale lookup) so tests
 * behave identically across environments. */
export function fmtDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** Human readout of the resolved window, e.g. "7 Jul – 13 Jul". */
export function formatScenarioWindow(endDate: string, lookbackDays = 6): string | null {
  const win = deriveScenarioWindow(endDate, lookbackDays);
  if (!win) return null;
  return `${fmtDay(win.start)} – ${fmtDay(win.end)}`;
}

/** The pool table shows all three outcome counts side by side. Not
 * converted is derived as total minus converted so
 * Total = Converted + Not converted always holds (clamped at 0
 * defensively). */
export function memberCounts(m: ScenarioPoolMember): {
  total: number;
  converted: number;
  notConverted: number;
} {
  return {
    total: m.viable_call_count,
    converted: m.converted_call_count,
    notConverted: Math.max(0, m.viable_call_count - m.converted_call_count),
  };
}

export function isTerminalPreviewStatus(
  status: JobStatus | undefined,
): boolean {
  return status === "succeeded" || status === "failed" || status === "dead";
}

/** Tanstack Query refetchInterval for the preview-status poll: 2s until
 * a confirmed terminal status, then stop. */
export function previewPollMs(status: JobStatus | undefined): number | false {
  return isTerminalPreviewStatus(status) ? false : ACTIVE_POLL_MS;
}

/** Tanstack Query refetchInterval for the persisted-run poll: 2s while
 * any tile is still building (so concurrent generations progress
 * without a manual refresh), otherwise 15s to detect policy changes made
 * by another administrator. */
export function runPollMs(
  generations: ScenarioGeneration[] | undefined,
): number {
  return generations?.some((g) => g.status === "building")
    ? ACTIVE_POLL_MS
    : SCENARIO_POLICY_POLL_MS;
}

/** Compare the report fields that identify the tile ordering and policy.
 * Window dates alone cannot identify a newly saved analysis of that window. */
export function sameScenarioPreview(
  left: ScenarioPreviewOut | null,
  right: ScenarioPreviewOut | null,
): boolean {
  if (!left || !right) return false;
  if (left.run_id || right.run_id) return left.run_id === right.run_id;
  const identity = (report: ScenarioPreviewOut) => JSON.stringify([
    report.team_id, report.window_start, report.window_end,
    report.config_revision ?? null, report.outcome_label ?? "",
    report.suggested_scenarios.map((scenario) => [
      scenario.title, scenario.product, scenario.scenario_type,
      scenario.customer_personality, scenario.pet_background, scenario.resistance_measures,
    ]),
  ]);
  return identity(left) === identity(right);
}
